import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { rank, type Destination } from './destinations';

export type PaletteMode = 'search' | 'new' | 'recent' | 'favourites';

interface CommandPaletteProps {
  open: boolean;
  mode: PaletteMode;
  /**
   * The destinations this person may be offered, already scoped to their role. Passed in
   * rather than imported, so the palette cannot reach past the caller's scoping to the full
   * catalogue — which is exactly what it used to do.
   */
  destinations: readonly Destination[];
  onClose: () => void;
  recent: string[];
  favourites: string[];
  onVisit: (id: string) => void;
  onToggleFavourite: (id: string) => void;
}

/**
 * One palette behind all four quick actions.
 *
 * WHY ONE, RATHER THAN FOUR SEPARATE SCREENS
 * Search, New, Recent and Favourites are four questions about the same set of places. Built
 * separately they become four pages to navigate between, and the moment somebody opens
 * "Recent" and wants something that is not there, they have to close it and start again.
 * A palette that opens on a mode and narrows as you type answers all four without ever
 * making the wrong choice a dead end — which is precisely what the four buttons were
 * before: they did nothing at all.
 *
 * KEYBOARD FIRST. Ctrl/Cmd+K from anywhere, arrows to move, Enter to go, Escape to leave.
 * The mouse works too, but an administrator doing the same three things every morning
 * should not have to reach for it.
 *
 * SCOPED BY ROLE, BY ITS CALLER. The destination list arrives as a prop and this component
 * never reaches for the full catalogue. Every mode — search included — narrows only what it
 * was given, so there is no path through here that can surface a page the current role
 * cannot open. That was the fault: a teacher typing "b" was offered Branding.
 *
 * ACCESSIBILITY IS NOT DECORATION HERE. The listbox is described by aria-activedescendant
 * rather than by moving focus, so the input keeps focus and a screen reader still announces
 * the highlighted row; the result count is announced politely on every change, because a
 * list that silently empties tells a sighted user everything and a blind user nothing.
 */
export function CommandPalette({
  open,
  mode,
  destinations,
  onClose,
  recent,
  favourites,
  onVisit,
  onToggleFavourite,
}: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const byId = useMemo(
    () => new Map(destinations.map((destination) => [destination.id, destination])),
    [destinations],
  );

  const results = useMemo(() => {
    // A query always searches everything. Opening "Recent" and typing should find a page
    // you have never visited — the mode chooses the STARTING point, not a cage.
    if (query.trim()) {
      return destinations.map((destination) => ({
        destination,
        score: rank(destination, query),
      }))
        .filter((entry): entry is { destination: Destination; score: number } => entry.score !== null)
        .sort((a, b) => a.score - b.score || a.destination.label.localeCompare(b.destination.label))
        .map((entry) => entry.destination);
    }

    if (mode === 'new') return destinations.filter((d) => d.creates);
    if (mode === 'recent') return recent.map((id) => byId.get(id)).filter(Boolean) as Destination[];
    if (mode === 'favourites') {
      return favourites.map((id) => byId.get(id)).filter(Boolean) as Destination[];
    }
    return [...destinations];
  }, [query, mode, recent, favourites, byId, destinations]);

  // Reset per opening, not per render: a palette that remembers last night's query makes
  // the first keystroke a deletion.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlighted(0);
    // Focus after paint, or the browser hands it back to whatever opened the dialog.
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, mode]);

  // Clamped rather than reset when results shrink: typing another character should keep
  // the highlight near where it was, not throw it back to the top.
  useEffect(() => {
    setHighlighted((current) => Math.min(current, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const go = useCallback(
    (destination: Destination) => {
      onVisit(destination.id);
      onClose();
      navigate(destination.path);
    },
    [navigate, onClose, onVisit],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Wraps, because a list that stops at the bottom makes the last item hard to reach
      // from the top of a long result set.
      setHighlighted((current) => (results.length ? (current + 1) % results.length : 0));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((current) =>
        results.length ? (current - 1 + results.length) % results.length : 0,
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const destination = results[highlighted];
      if (destination) go(destination);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  // Keeps the highlighted row visible when moving by keyboard through a scrolled list.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[data-highlighted="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  if (!open) return null;

  const titles: Record<PaletteMode, string> = {
    search: t('command.search'),
    new: t('command.new'),
    recent: t('command.recent'),
    favourites: t('command.favourites'),
  };

  return (
    <>
      <div className="command-overlay" onClick={onClose} role="presentation" />
      <div
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-label={titles[mode]}
        onKeyDown={onKeyDown}
      >
        <div className="command-inputRow">
          <Icon name="search" size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            className="command-input"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('command.placeholder')}
            aria-label={t('command.placeholder')}
            aria-controls="command-results"
            aria-activedescendant={results[highlighted] ? `command-${results[highlighted].id}` : undefined}
            role="combobox"
            aria-expanded="true"
            autoComplete="off"
          />
          <kbd className="command-hint" aria-hidden="true">
            Esc
          </kbd>
        </div>

        <ul id="command-results" ref={listRef} className="command-results" role="listbox" aria-label={titles[mode]}>
          {results.map((destination, index) => (
            <li
              key={destination.id}
              id={`command-${destination.id}`}
              role="option"
              aria-selected={index === highlighted}
              data-highlighted={index === highlighted}
              className={`command-result ${index === highlighted ? 'command-result--active' : ''}`}
              // Pointer moves the highlight rather than a separate hover style, so the
              // keyboard and the mouse never disagree about which row Enter would open.
              onMouseMove={() => setHighlighted(index)}
              onClick={() => go(destination)}
            >
              <Icon name={destination.icon} size={18} aria-hidden="true" />
              <span className="command-resultLabel">{destination.label}</span>
              <span className="command-resultGroup">{destination.group}</span>
              <button
                type="button"
                className="command-star"
                aria-label={
                  favourites.includes(destination.id)
                    ? `${t('command.unfavourite')} ${destination.label}`
                    : `${t('command.favourite')} ${destination.label}`
                }
                aria-pressed={favourites.includes(destination.id)}
                onClick={(event) => {
                  // Or clicking the star would also navigate, and pinning something would
                  // close the palette you were pinning it from.
                  event.stopPropagation();
                  onToggleFavourite(destination.id);
                }}
              >
                <Icon name="star" size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>

        {results.length === 0 && (
          <p className="command-empty">
            {mode === 'favourites' && !query
              ? t('command.noFavourites')
              : mode === 'recent' && !query
                ? t('command.noRecent')
                : t('command.noResults')}
          </p>
        )}

        {/* Announced politely: a list that empties as you type is obvious on screen and
            silent to a screen reader without this. */}
        <span className="sr-only" role="status" aria-live="polite">
          {t('command.resultCount', { count: results.length })}
        </span>
      </div>
    </>
  );
}
